/**
 * Caption Presets - Design system for caption styles
 * Each preset is a complete visual definition that can be:
 * 1. Rendered in the UI (React/CSS)
 * 2. Converted to FFmpeg filters for export
 */

import { ANIMATION_TYPES } from './captionTypes';

/**
 * @typedef {Object} CaptionPreset
 * @property {string} id - Unique preset identifier
 * @property {string} name - Display name
 * @property {string} description - Short description
 * @property {string} font - Font family
 * @property {number} weight - Font weight (400-900)
 * @property {'uppercase'|'lowercase'|'sentence'} textCase - Text transform
 * @property {string} textColor - Primary text color (hex)
 * @property {string} highlightColor - Highlight/emphasis color (hex)
 * @property {string} bgColor - Background color (hex or 'transparent')
 * @property {string} strokeColor - Text outline color (hex)
 * @property {number} strokeWidth - Stroke width in pixels
 * @property {string} animation - Animation type from ANIMATION_TYPES
 * @property {boolean} wordByWord - Whether to highlight word by word
 * @property {number} maxWordsPerLine - Maximum words per line
 * @property {Object} preview - Preview text configuration
 */

export const CAPTION_PRESETS = {
    karaoke: {
        id: 'karaoke',
        name: 'Karaoke',
        description: 'Word-by-word highlight',
        font: 'Inter',
        weight: 700,
        fontSize: 48,
        textCase: 'uppercase',
        textColor: '#FFFFFF',
        highlightColor: '#00FF66',
        bgColor: 'transparent',
        strokeColor: '#000000',
        strokeWidth: 3,
        animation: ANIMATION_TYPES.WORD_HIGHLIGHT,
        wordByWord: true,
        maxWordsPerLine: 4,
        lineSpacing: 1.2,
        preview: { base: 'TO', highlight: 'GET', suffix: 'STARTED' },
        // Animation timing
        animationDuration: 0.15,
        animationEasing: 'ease-out'
    },
    beasty: {
        id: 'beasty',
        name: 'Beasty',
        description: 'Scale punch effect',
        font: 'Anton',
        weight: 800,
        fontSize: 56,
        textCase: 'uppercase',
        textColor: '#FFFFFF',
        highlightColor: '#FFB800',
        bgColor: 'transparent',
        strokeColor: '#000000',
        strokeWidth: 4,
        animation: ANIMATION_TYPES.SCALE_PUNCH,
        wordByWord: true,
        maxWordsPerLine: 3,
        lineSpacing: 1.1,
        preview: { base: 'CHOOSE A', highlight: 'STYLE', suffix: '' },
        animationDuration: 0.2,
        animationEasing: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
    },
    deepdiver: {
        id: 'deepdiver',
        name: 'Deep Diver',
        description: 'Phrase fade + slide',
        font: 'Inter',
        weight: 400,
        fontSize: 36,
        textCase: 'sentence',
        textColor: '#FFFFFF',
        highlightColor: '#888888',
        bgColor: 'rgba(0,0,0,0.5)',
        strokeColor: 'transparent',
        strokeWidth: 0,
        animation: ANIMATION_TYPES.FADE_SLIDE,
        wordByWord: false,
        maxWordsPerLine: 8,
        lineSpacing: 1.4,
        preview: { base: 'to get', highlight: 'started', suffix: '' },
        animationDuration: 0.3,
        animationEasing: 'ease-in-out'
    },
    youshaei: {
        id: 'youshaei',
        name: 'Youshaei',
        description: 'Keyword accent',
        font: 'Inter',
        weight: 600,
        fontSize: 42,
        textCase: 'sentence',
        textColor: '#FFFFFF',
        highlightColor: '#04FF00',
        bgColor: 'transparent',
        strokeColor: '#000000',
        strokeWidth: 2,
        animation: ANIMATION_TYPES.OPACITY_REVEAL,
        wordByWord: false,
        maxWordsPerLine: 6,
        lineSpacing: 1.3,
        preview: { base: 'TO', highlight: 'GET', suffix: 'STARTED' },
        animationDuration: 0.25,
        animationEasing: 'ease-out'
    },
    podp: {
        id: 'podp',
        name: 'Pod P',
        description: 'Podcast-safe',
        font: 'Roboto Condensed',
        weight: 700,
        fontSize: 40,
        textCase: 'uppercase',
        textColor: '#000000',
        highlightColor: '#04FF00',
        bgColor: '#04FF00',
        strokeColor: 'transparent',
        strokeWidth: 0,
        animation: ANIMATION_TYPES.NONE,
        wordByWord: false,
        maxWordsPerLine: 5,
        lineSpacing: 1.2,
        preview: { base: 'GET', highlight: 'STARTED', suffix: '' },
        animationDuration: 0,
        animationEasing: 'linear'
    },
    mozi: {
        id: 'mozi',
        name: 'Mozi',
        description: 'Word pop-in',
        font: 'Poppins',
        weight: 700,
        fontSize: 44,
        textCase: 'sentence',
        textColor: '#FFFFFF',
        highlightColor: '#FF00FF',
        bgColor: 'transparent',
        strokeColor: '#000000',
        strokeWidth: 2,
        animation: ANIMATION_TYPES.POP_IN,
        wordByWord: true,
        maxWordsPerLine: 4,
        lineSpacing: 1.2,
        preview: { base: 'CHOOSE A', highlight: 'STYLE', suffix: '' },
        animationDuration: 0.15,
        animationEasing: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
    },
    popline: {
        id: 'popline',
        name: 'Popline',
        description: 'Line slide-up',
        font: 'Montserrat',
        weight: 800,
        fontSize: 46,
        textCase: 'uppercase',
        textColor: '#FFFFFF',
        highlightColor: '#FFFFFF',
        bgColor: 'transparent',
        strokeColor: '#000000',
        strokeWidth: 3,
        animation: ANIMATION_TYPES.SLIDE_UP,
        wordByWord: false,
        maxWordsPerLine: 4,
        lineSpacing: 1.1,
        preview: { base: 'TO GET', highlight: 'STARTED', suffix: '' },
        animationDuration: 0.25,
        animationEasing: 'ease-out'
    },
    simple: {
        id: 'simple',
        name: 'Simple',
        description: 'Clean & minimal',
        font: 'Inter',
        weight: 400,
        fontSize: 32,
        textCase: 'sentence',
        textColor: '#FFFFFF',
        highlightColor: '#FFFFFF',
        bgColor: 'rgba(0,0,0,0.6)',
        strokeColor: 'transparent',
        strokeWidth: 0,
        animation: ANIMATION_TYPES.FADE,
        wordByWord: false,
        maxWordsPerLine: 10,
        lineSpacing: 1.4,
        preview: { base: 'CHOOSE A', highlight: '', suffix: '' },
        animationDuration: 0.2,
        animationEasing: 'ease-in-out'
    },
    thinkmedia: {
        id: 'thinkmedia',
        name: 'Think Media',
        description: 'Keyword highlight',
        font: 'Inter',
        weight: 600,
        fontSize: 40,
        textCase: 'sentence',
        textColor: '#FFFFFF',
        highlightColor: '#04FF00',
        bgColor: 'transparent',
        strokeColor: '#000000',
        strokeWidth: 2,
        animation: ANIMATION_TYPES.KEYWORD_SWAP,
        wordByWord: false,
        maxWordsPerLine: 5,
        lineSpacing: 1.3,
        preview: { base: 'TO GET', highlight: 'STARTED', suffix: '' },
        animationDuration: 0.2,
        animationEasing: 'ease-out'
    }
};

/**
 * Get a preset by ID
 * @param {string} presetId 
 * @returns {CaptionPreset|null}
 */
export function getPreset(presetId) {
    return CAPTION_PRESETS[presetId] || CAPTION_PRESETS.karaoke;
}

/**
 * Get all preset IDs as array
 * @returns {string[]}
 */
export function getPresetIds() {
    return Object.keys(CAPTION_PRESETS);
}

/**
 * Convert preset to CSS styles object for React
 * @param {CaptionPreset} preset 
 * @returns {Object}
 */
export function presetToCssStyles(preset) {
    const textTransform = preset.textCase === 'uppercase' ? 'uppercase'
        : preset.textCase === 'lowercase' ? 'lowercase'
            : 'none';

    const textShadow = preset.strokeWidth > 0 && preset.strokeColor !== 'transparent'
        ? `${preset.strokeWidth}px ${preset.strokeWidth}px 0 ${preset.strokeColor}, 
           -${preset.strokeWidth}px -${preset.strokeWidth}px 0 ${preset.strokeColor},
           ${preset.strokeWidth}px -${preset.strokeWidth}px 0 ${preset.strokeColor},
           -${preset.strokeWidth}px ${preset.strokeWidth}px 0 ${preset.strokeColor}`
        : 'none';

    return {
        fontFamily: preset.font,
        fontWeight: preset.weight,
        fontSize: `${preset.fontSize}px`,
        textTransform,
        color: preset.textColor,
        textShadow,
        backgroundColor: preset.bgColor === 'transparent' ? 'transparent' : preset.bgColor,
        lineHeight: preset.lineSpacing
    };
}

export default CAPTION_PRESETS;
